import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { TuhaIcon } from "@/components/TuhaLogo";

// One-time blocking gate: a logged-in user must accept Terms + AI advisory before using the app.
export default function TermsGate() {
  const { user, setUser } = useAuth();
  const { pathname } = useLocation();
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!user) return null;
  if (user.profile?.terms_accepted_at) return null;
  // Don't block the public legal pages (opened from the gate's own links).
  if (pathname === "/terms" || pathname === "/privacy") return null;

  const accept = async () => {
    if (!checked || saving) return;
    setSaving(true);
    try {
      const { data } = await api.post("/auth/accept-terms");
      setUser({ ...user, profile: { ...user.profile, terms_accepted_at: data.terms_accepted_at } });
    } catch (e) {
      setSaving(false);
    }
  };

  return (
    <Dialog open>
      <DialogContent
        className="max-w-md rounded-3xl max-h-[90dvh] overflow-y-auto [&>button:last-child]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="terms-gate-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <TuhaIcon size={28} />
            <DialogTitle className="font-serif-display text-2xl text-foreground">Welcome to KAAYA</DialogTitle>
          </div>
          <DialogDescription className="text-foreground/75 pt-1">
            Before you start, please review and accept our terms.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-2xl bg-muted/60 border border-border p-4 flex gap-3" data-testid="terms-gate-advisory">
          <AlertTriangle size={18} className="text-secondary shrink-0 mt-0.5" />
          <p className="text-xs text-foreground/80 leading-relaxed">
            KAAYA uses AI and <strong>can make mistakes</strong>. Its guidance is informational only and
            is <strong>not medical advice</strong>. Always use your own discretion and consult a qualified
            professional for medical or dietary decisions.
          </p>
        </div>

        <label className="flex items-start gap-3 mt-2 cursor-pointer select-none">
          <Checkbox
            checked={checked}
            onCheckedChange={(v) => setChecked(!!v)}
            className="mt-0.5"
            data-testid="terms-gate-checkbox"
          />
          <span className="text-sm text-foreground/85 leading-relaxed">
            I accept the{" "}
            <a href="/terms" target="_blank" rel="noreferrer" className="text-secondary underline" data-testid="terms-gate-terms-link">Terms &amp; Conditions</a>,{" "}
            <a href="/privacy" target="_blank" rel="noreferrer" className="text-secondary underline" data-testid="terms-gate-privacy-link">Privacy Policy</a>, and the AI advisory above.
          </span>
        </label>

        <Button
          onClick={accept}
          disabled={!checked || saving}
          className="w-full mt-2 rounded-full bg-primary hover:bg-primary/90 h-11"
          data-testid="terms-gate-accept-btn"
        >
          {saving ? "Saving…" : "Accept & continue"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
